import * as React from 'react'
import Card, { CardClassKey } from '@mui/material/Card'
import CardContent, { CardContentClassKey } from '@mui/material/CardContent'
import Tab, { TabClassKey } from '@mui/material/Tab'
import Tabs, { TabsClassKey } from '@mui/material/Tabs'
import classNames from 'classnames'
import iassign from 'immutable-assign'
import intersection from 'lodash/intersection'
import DirectSchedulingTab from 'modules/direct-scheduling/DirectSchedulingTab'

import AdminAccount from '../../models/AdminAccount'
import {
    AccessPatientsTab,
    AccessPaymentsTab,
    Amplify,
    Chairfill,
    DexVoice,
    DirectScheduling,
    Flow,
    ManageAllPractices,
    ManageAmplify,
    ManageDexVoice,
    ManageDirectScheduling,
    ManageFlow,
    ManageIntegrations,
    ManagePayments,
    ProductType,
    QuickviewTab,
    SimplifeyePayments,
} from '../../models/enums'
import AmplifyTab from '../amplify/AmplifyTab'
import ChairfillTab from '../chairfill/ChairfillTab'
import DexVoiceContainer from '../dexvoice/DexVoiceContainer'
import PaymentsTabContainer from '../payments/PaymentsTabContainer'
import PracticeHeader from '../shared/PracticeHeader'

import FlowTabContainer from './flow/FlowTabContainer'
import { Integrations } from './integrations/Integrations'
import LocationsTab from './locations/LocationsTab'
import PatientsTab from './patients/PatientsTab'
import { resetSearchPracticePatients } from './patients/v2actions'
import StaffTab from './staff/StaffTab'
import { savePractice, saveSelectedPractice } from './actions'

import './PracticeQuickview.sass'

export type PracticeQuickviewProps = {
    practice: Models.Practice
    account: AdminAccount
    practiceSearchTerms: Models.SearchTerms
    selectedTab?: QuickviewTab
    saveSelectedPractice: typeof saveSelectedPractice
    resetSearchPracticePatients: typeof resetSearchPracticePatients
}

export type PracticeQuickviewDispatch = {
    savePractice: typeof savePractice
}

export type PracticeQuickviewState = {
    selected?: QuickviewTab
}

type Props = PracticeQuickviewProps & PracticeQuickviewDispatch

type CardContentClassOverrides = {
    readonly [key in CardContentClassKey]?: string
}

type CardClassOverrides = {
    readonly [key in CardClassKey]?: string
}

type TabsClassOverrides = {
    readonly [key in TabsClassKey]?: string
}

type TabClassOverrides = {
    readonly [key in TabClassKey]?: string
}

class PracticeQuickview extends React.Component<Props, PracticeQuickviewState> {
    private restoredTab: QuickviewTab | undefined = undefined
    private restorationTimeoutId: NodeJS.Timeout | null = null

    constructor(props: Props) {
        super(props)
        this.state = {
            selected: props.selectedTab,
        }
    }

    componentDidUpdate(prevProps: Props) {
        // NOTE: we are doing this to refresh tabs and their associated products
        if (prevProps.selectedTab !== this.props.selectedTab && this.props.selectedTab === undefined) {
            const previousTab = prevProps.selectedTab

            if (this.restorationTimeoutId) {
                clearTimeout(this.restorationTimeoutId)
                this.restorationTimeoutId = null
            }

            if (previousTab && this.restoredTab !== previousTab) {
                this.restoredTab = previousTab
                this.setState({ selected: undefined }, () => {
                    const restore = () => {
                        if (this.props.selectedTab === undefined && this.state.selected === undefined) {
                            this.setState({ selected: previousTab })
                        }
                        this.restorationTimeoutId = null
                    }

                    if (typeof requestAnimationFrame !== 'undefined') {
                        requestAnimationFrame(restore)
                    } else {
                        this.restorationTimeoutId = setTimeout(restore, 0)
                    }
                })
            } else {
                this.setState({ selected: undefined })
            }
        } else if (this.props.selectedTab !== undefined) {
            if (this.restoredTab !== undefined) {
                this.restoredTab = undefined
            }
        }
    }

    componentWillUnmount() {
        if (this.restorationTimeoutId) {
            clearTimeout(this.restorationTimeoutId)
            this.restorationTimeoutId = null
        }
    }

    hasPermission(permissions?: Models.Permission[]) {
        if (permissions) {
            return permissions.some(this.props.account.hasAccess.bind(this.props.account))
        }
        return true
    }

    hasProductTab(productTypes: ProductType | ProductType[], permissions?: Models.Permission[]) {
        const tabs = Array.isArray(productTypes) ? productTypes.map(p => p.value) : [productTypes.value]
        const products = this.props.practice.products.map(p => p.value)
        const hasProduct = intersection(products, tabs).length > 0
        return hasProduct && this.hasPermission(permissions)
    }
    onSwitchTab(tabname: QuickviewTab) {
        this.setState(
            iassign(this.state, (next: PracticeQuickviewState) => {
                next.selected = tabname
                return next
            }),
        )
    }
    isProductTabActive(tabname: QuickviewTab) {
        const { practice } = this.props
        const tabProductMap: Partial<Record<QuickviewTab, string[]>> = {
            [QuickviewTab.Amplify]: [Amplify.value],
            [QuickviewTab.DirectScheduling]: [DirectScheduling.value],
            [QuickviewTab.Payments]: [SimplifeyePayments.value],
            [QuickviewTab.Chairfill]: [Chairfill.value],
            [QuickviewTab.Integrations]: [Chairfill.value, DirectScheduling.value, SimplifeyePayments.value],
        }
        const productValues = tabProductMap[tabname]
        if (!productValues) {
            return true
        }
        return practice.products.some(p => productValues.includes(p.value) && p.active)
    }

    onSelectTab(tabname: QuickviewTab, practiceId: string) {
        if (!this.isProductTabActive(tabname)) {
            return
        }
        this.onSwitchTab(tabname)
        if (tabname === QuickviewTab.Patients) {
            this.props.resetSearchPracticePatients(practiceId)
        }
        this.props.saveSelectedPractice({ id: practiceId, selectedTab: tabname })
    }

    render() {
        const { practice, account } = this.props
        const { selected } = this.state

        const cardContentClassOverrides: CardContentClassOverrides = {
            root: 'card-content-root',
        }

        const cardClassOverrides: CardClassOverrides = {
            root: 'card-root',
        }

        const tabsClassOverrides: TabsClassOverrides = {
            flexContainer: 'tabs-container',
            scroller: 'tabs-scroller',
            indicator: 'tabs-indicator',
        }

        const tabClassOverrides: TabClassOverrides = {
            wrapped: 'tab-wrapper',
            labelIcon: 'tab-icon',
            iconWrapper: 'tab-icon-wrapper',
        }

        const tabLabelClass = 'label tab-label-container'

        const hasPayments = this.hasProductTab(SimplifeyePayments, [ManagePayments, ManageAllPractices])
        const isPMG = !!practice.vyneGroupId

        return (
            <div className={classNames('practice-quickview')} id={`practice-quickview-${practice.id}`}>
                <Card
                    className={classNames('card')}
                    classes={cardClassOverrides}
                    style={{ borderColor: isPMG ? '#346bc7' : undefined }}
                >
                    <CardContent classes={cardContentClassOverrides}>
                        <PracticeHeader practice={practice} />
                    </CardContent>
                    <Tabs
                        className={classNames('tab-bar')}
                        classes={tabsClassOverrides}
                        onChange={(_e, value) => this.onSelectTab(value, practice.id)}
                        value={this.state.selected || false}
                        indicatorColor="primary"
                        textColor="inherit"
                    >
                        <Tab
                            className={classNames('tab', {
                                selected: this.state.selected === QuickviewTab.Practices,
                            })}
                            label={<span className={tabLabelClass}>Practice(s)</span>}
                            value={QuickviewTab.Practices}
                            icon={<i className="material-icons">place</i>}
                            classes={tabClassOverrides}
                        />
                        {this.hasProductTab(Flow, [ManageFlow, ManageAllPractices]) &&
                            practice.products.some(p => p.value === Flow.value && p.active) && (
                                <Tab
                                    className={classNames('tab', {
                                        selected: this.state.selected === QuickviewTab.Flow,
                                    })}
                                    label={<span className={tabLabelClass}>Flow</span>}
                                    value={QuickviewTab.Flow}
                                    classes={tabClassOverrides}
                                />
                            )}
                        {this.hasProductTab(DexVoice, [ManageDexVoice, ManageAllPractices]) &&
                            practice.products.some(p => p.value === DexVoice.value && p.active) && (
                                <Tab
                                    className={classNames('tab', {
                                        selected: this.state.selected === QuickviewTab.DexVoice,
                                    })}
                                    label={<span className={tabLabelClass}>DexVoice</span>}
                                    value={QuickviewTab.DexVoice}
                                    classes={tabClassOverrides}
                                />
                            )}
                        {this.hasProductTab(Amplify, [ManageAmplify, ManageAllPractices]) && (
                            <Tab
                                className={classNames(
                                    'tab',
                                    {
                                        inactive: !practice.products.some(p => p.value === Amplify.value && p.active),
                                    },
                                    { selected: this.state.selected === QuickviewTab.Amplify },
                                )}
                                label={<span className={tabLabelClass}>Live Webchat</span>}
                                value={QuickviewTab.Amplify}
                                classes={tabClassOverrides}
                            />
                        )}
                        {this.hasProductTab(DirectScheduling, [ManageDirectScheduling, ManageAllPractices]) && (
                            <Tab
                                className={classNames(
                                    'tab',
                                    {
                                        inactive: !practice.products.some(
                                            p => p.value === DirectScheduling.value && p.active,
                                        ),
                                    },
                                    { selected: this.state.selected === QuickviewTab.DirectScheduling },
                                )}
                                label={<span className={tabLabelClass}>Online Scheduling</span>}
                                value={QuickviewTab.DirectScheduling}
                                classes={tabClassOverrides}
                            />
                        )}
                        {this.hasPermission([AccessPaymentsTab]) && hasPayments && (
                            <Tab
                                className={classNames(
                                    'tab',
                                    {
                                        inactive: !practice.products.some(
                                            p => p.value === SimplifeyePayments.value && p.active,
                                        ),
                                    },
                                    { selected: this.state.selected === QuickviewTab.Payments },
                                )}
                                label={<span className={tabLabelClass}>Simplifeye Payments</span>}
                                value={QuickviewTab.Payments}
                                classes={tabClassOverrides}
                            />
                        )}
                        {this.hasProductTab(Chairfill) && (
                            <Tab
                                className={classNames(
                                    'tab',
                                    {
                                        inactive: !practice.products.some(p => p.value === Chairfill.value && p.active),
                                    },
                                    { selected: this.state.selected === QuickviewTab.Chairfill },
                                )}
                                label={<span className={tabLabelClass}>Campaigns</span>}
                                value={QuickviewTab.Chairfill}
                                classes={tabClassOverrides}
                            />
                        )}
                        {this.hasPermission([AccessPatientsTab]) && (
                            <Tab
                                className={classNames('tab', {
                                    selected: this.state.selected === QuickviewTab.Patients,
                                })}
                                label={<span className="label">Patients</span>}
                                value={QuickviewTab.Patients}
                                classes={tabClassOverrides}
                            />
                        )}
                        <Tab
                            className={classNames('tab', { selected: this.state.selected === QuickviewTab.Staff })}
                            label={<span className={tabLabelClass}>Staff</span>}
                            value={QuickviewTab.Staff}
                            icon={<i className="material-icons">group</i>}
                            classes={tabClassOverrides}
                        />
                        {this.hasProductTab(
                            [Chairfill, DirectScheduling, SimplifeyePayments],
                            [
                                ManageIntegrations,
                                ManageAmplify,
                                ManagePayments,
                                ManageDirectScheduling,
                                ManageAllPractices,
                            ],
                        ) && (
                            <Tab
                                className={classNames(
                                    'tab',
                                    {
                                        inactive: !practice.products.some(
                                            p =>
                                                [
                                                    Chairfill.value,
                                                    DirectScheduling.value,
                                                    SimplifeyePayments.value,
                                                ].includes(p.value) && p.active,
                                        ),
                                    },
                                    {
                                        selected: this.state.selected === QuickviewTab.Integrations,
                                    },
                                )}
                                label={<span className={tabLabelClass}>Integrations</span>}
                                value={QuickviewTab.Integrations}
                                classes={tabClassOverrides}
                            />
                        )}
                    </Tabs>
                    {selected === QuickviewTab.Flow && (
                        <CardContent>
                            <FlowTabContainer practice={practice} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.DexVoice && (
                        <CardContent>
                            <DexVoiceContainer practice={practice} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Amplify && (
                        <CardContent classes={cardContentClassOverrides}>
                            <AmplifyTab practice={practice} account={account} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.DirectScheduling && (
                        <CardContent classes={cardContentClassOverrides}>
                            <DirectSchedulingTab practice={practice} account={account} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Payments && (
                        <CardContent>
                            <PaymentsTabContainer practice={practice} account={account} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Staff && (
                        <CardContent>
                            <StaffTab practice={practice} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Practices && (
                        <CardContent>
                            <LocationsTab
                                practice={practice}
                                hasPayments={hasPayments}
                                practiceSearchTerms={this.props.practiceSearchTerms}
                            />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Integrations && (
                        <CardContent style={{ padding: '8px' }}>
                            <Integrations practice={practice} account={account} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Patients && (
                        <CardContent>
                            <PatientsTab practice={practice} />
                        </CardContent>
                    )}
                    {selected === QuickviewTab.Chairfill && (
                        <CardContent>
                            <ChairfillTab practice={practice} account={account} />
                        </CardContent>
                    )}
                </Card>
            </div>
        )
    }
}

export default PracticeQuickview
